import { useState } from "react";
import { Download, Eye, FileText, RotateCcw } from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Area, Card, Confirm, IconBtn, Input, OBtn, PageTitle, Pill, Select, Skel, useSaver } from "@/admin/ui";
import { adminApi, downloadBlob, fmtDate, useAdmin, useRefresh } from "@/admin/lib";
import { API, inr } from "@/lib/api";

const Row = ({ k, v }) => <div className="flex justify-between gap-4 border-b border-line/60 py-2 text-sm last:border-0"><span className="text-muted2">{k}</span><span className="text-right font-semibold text-navy">{v || "-"}</span></div>;

const Detail = ({ o, onClose, onDone }) => {
  const [note, setNote] = useState("");
  const [saving, run] = useSaver();
  const refund = async () => {
    const r = await run(() => adminApi.post(`/admin/orders/${o.order_id}/refund`, { note }), "Order marked as refunded");
    if (r) { onDone(); onClose(); }
  };
  return (
    <Sheet open onOpenChange={onClose}>
      <SheetContent className="w-full overflow-y-auto bg-white sm:max-w-lg" data-testid="order-detail">
        <SheetTitle className="font-heading text-xl text-navy">Order {o.order_id}</SheetTitle>
        <div className="mt-2"><Pill value={o.status} testId="order-detail-status" /></div>
        <div className="mt-6 space-y-6">
          <div><h3 className="mb-2 text-sm font-bold">Customer</h3>
            <Row k="Name" v={o.customer?.name} /><Row k="Email" v={o.customer?.email} /><Row k="Phone" v={o.customer?.phone} /><Row k="GSTIN" v={o.customer?.gst_number} /><Row k="State" v={o.customer?.billing_state} />
          </div>
          <div><h3 className="mb-2 text-sm font-bold">Payment</h3>
            <Row k="Item" v={o.item?.name} /><Row k="Base" v={inr(o.base_amount)} /><Row k={`GST (${o.gst_rate}%)`} v={inr(o.gst_amount)} /><Row k="Total" v={inr(o.total_amount)} />
            <Row k="Razorpay order" v={o.razorpay_order_id} /><Row k="Payment ID" v={o.payment_id} /><Row k="Invoice" v={o.invoice_number} /><Row k="Mode" v={o.mode} />
            <Row k="Created" v={fmtDate(o.created_at)} /><Row k="Paid" v={fmtDate(o.paid_at)} />
            {o.failure_reason && <Row k="Failure reason" v={o.failure_reason} />}{o.refund_note && <Row k="Refund note" v={o.refund_note} />}
          </div>
          <div><h3 className="mb-2 text-sm font-bold">History</h3>
            {(o.status_history || []).map((h, i) => <Row key={i} k={fmtDate(h.at)} v={`${h.status}${h.source ? ` (${h.source})` : ""}`} />)}
          </div>
          {["paid", "refunded"].includes(o.status) && <a href={`${API}/orders/${o.order_id}/invoice`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full bg-cta px-5 py-2.5 text-sm font-bold text-navy" data-testid="order-invoice-btn"><FileText className="h-4 w-4" />Download invoice</a>}
          {o.status === "paid" && (
            <div className="rounded-2xl border border-line p-4">
              <h3 className="text-sm font-bold">Mark as refunded</h3>
              <p className="mb-3 mt-1 text-xs text-muted2">If Razorpay keys are configured, a full refund is issued through Razorpay automatically.</p>
              <Area rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Refund note / reason" testId="refund-note-input" />
              <Confirm title="Refund this order?" description="The order will be marked refunded and, if keys are configured, refunded via Razorpay." actionLabel="Refund" onConfirm={refund}>
                <OBtn className="mt-3" disabled={saving} testId="refund-btn"><RotateCcw className="h-4 w-4" />Mark as refunded</OBtn>
              </Confirm>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default function Orders() {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [sel, setSel] = useState(null);
  const refresh = useRefresh();
  const { data, isLoading } = useAdmin(`/admin/records/orders?limit=100${q ? `&q=${encodeURIComponent(q)}` : ""}${status ? `&status=${status}` : ""}`);
  const items = data?.items || [];
  return (
    <>
      <PageTitle title="Orders & Payments" subtitle={`${data?.total ?? 0} orders`}>
        <OBtn onClick={() => downloadBlob("/admin/export/orders", "orders.csv", status ? { status } : {})} testId="orders-export-btn"><Download className="h-4 w-4" />Export CSV</OBtn>
      </PageTitle>
      <Card>
        <div className="mb-4 grid gap-3 sm:grid-cols-3">
          <Input placeholder="Search order ID, customer, payment ID" value={q} onChange={(e) => setQ(e.target.value)} className="sm:col-span-2" testId="orders-search-input" />
          <Select value={status} onChange={(e) => setStatus(e.target.value)} options={[{ value: "", label: "All statuses" }, "paid", "created", "failed", "refunded"]} testId="orders-status-filter" />
        </div>
        {isLoading ? <Skel /> : items.length ? (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[800px] text-sm" data-testid="orders-table">
              <thead><tr className="border-b border-line text-left text-xs uppercase tracking-wider text-muted2">{["Order", "Customer", "Item", "Total", "Status", "Date", ""].map((h) => <th key={h} className="py-2 pr-3 font-bold">{h}</th>)}</tr></thead>
              <tbody>{items.map((o) => (
                <tr key={o.order_id} className="border-b border-line/60 last:border-0" data-testid={`order-row-${o.order_id}`}>
                  <td className="py-3 pr-3 font-semibold text-navy">{o.order_id}</td>
                  <td className="py-3 pr-3 text-body">{o.customer?.name}<p className="text-xs">{o.customer?.email}</p></td>
                  <td className="py-3 pr-3 text-body">{o.item?.name}</td>
                  <td className="py-3 pr-3 font-semibold text-navy">{inr(o.total_amount)}</td>
                  <td className="py-3 pr-3"><Pill value={o.status} /></td>
                  <td className="whitespace-nowrap py-3 pr-3 text-xs text-muted2">{fmtDate(o.created_at)}</td>
                  <td className="py-3"><IconBtn label="View" onClick={() => setSel(o)} testId={`order-view-${o.order_id}`}><Eye className="h-4 w-4" /></IconBtn></td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        ) : <p className="py-10 text-center text-sm text-muted2" data-testid="orders-empty">No orders yet. Orders appear here when customers start a checkout.</p>}
      </Card>
      {sel && <Detail o={sel} onClose={() => setSel(null)} onDone={() => refresh("/admin/records/orders")} />}
    </>
  );
}
